// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// ── What is not yet settled ──────────────────────────────────────────────────
//
// The Decisions tab assembles this for the owner/architect meeting. It is
// extracted here because the app now also opens on Decisions when a project
// has any, and a landing rule that counted them its own way would eventually
// disagree with the page it sends you to -- offering a tab that then says
// "nothing outstanding" is the kind of small lie that costs trust in every
// other number on the screen.
//
// One pass, and each line lands in at most one bucket, so a Needs Review
// allowance is not counted twice.

import { walkItems } from "./calculations";

export type DecisionRow = { id: string; desc: string; div: string; amt: number };

export type OpenDecisions = {
  allowances: DecisionRow[];
  needsReview: DecisionRow[];
  altTBD: any[];
  openRfis: any[];
  /** Everything still unresolved, as one number. */
  count: number;
  /** Cost carried at a price nobody has committed to. */
  exposure: number;
  /** How far the total could move on the alternates still in play. */
  altSwing: number;
};

const byAmt = (a: { amt: number }, b: { amt: number }) => b.amt - a.amt;
const sum = (rows: { amt: number }[]) => rows.reduce((s, r) => s + r.amt, 0);

export function gatherDecisions(
  divisions: any[] | null | undefined,
  itemState: Record<string, any> | null | undefined,
  altTotals: { add?: any[]; ded?: any[] } | null | undefined,
  rfis: any[] | null | undefined,
): OpenDecisions {
  const allowances: DecisionRow[] = [];
  const needsReview: DecisionRow[] = [];
  const st = itemState || {};

  (divisions || []).forEach((div: any) =>
    (div?.subcategories || []).forEach((sub: any) => {
      [...walkItems(sub?.rows || [])].forEach((item: any) => {
        const li: any = st[item.id] || {};
        if (li.included === false) return;
        if (li.altType) return;                 // alternates are their own bucket
        const amt = (li.qty || 0) * (li.unitCost || 0);
        if (amt === 0) return;                  // an unpriced line is not a decision yet
        const row: DecisionRow = {
          id: item.id,
          desc: li.description || item.description || "—",
          div: div.code,
          amt,
        };
        if (li.costStatus === "Needs Review") needsReview.push(row);
        else if (li.costStatus === "Allowance") allowances.push(row);
      });
    }),
  );

  allowances.sort(byAmt);
  needsReview.sort(byAmt);

  const altTBD = [...(altTotals?.add || []), ...(altTotals?.ded || [])]
    .filter((a: any) => (a.altStatus || "TBD") === "TBD")
    .sort((a: any, b: any) => b.amt - a.amt);

  const openRfis = (rfis || []).filter((r: any) => (r.status || "Open") !== "Closed");

  return {
    allowances, needsReview, altTBD, openRfis,
    count: altTBD.length + allowances.length + needsReview.length + openRfis.length,
    exposure: sum(allowances) + sum(needsReview),
    altSwing: altTBD.reduce((s: number, a: any) => s + Math.abs(a.amt), 0),
  };
}
